import {TopicpromoCinemaSlide} from "./topicpromo_cinema_slide";
import {FeatureTrackingLabels} from "../types";
import {TrackingService} from "./tracking_service";
import {OcCinemaV1Events} from "@otto-ec/otto-components/cinema";

export class TopicpromoCinema {
    private readonly trackingService: TrackingService;
    readonly slides: Array<TopicpromoCinemaSlide>
    private readonly featureIndex: string;
    private readonly featureOrder: number;
    private readonly featureLabels: FeatureTrackingLabels;
    private readonly domElement: Element;

    constructor(topicpromoDom: Element) {
        this.domElement = topicpromoDom;
        this.featureIndex = this.getFeatureIndex(topicpromoDom);
        this.featureOrder = this.getFeatureOrder(topicpromoDom);
        this.featureLabels = this.getFeatureLabels(topicpromoDom);
        this.trackingService = new TrackingService()

        const slideContainers = Array.from(topicpromoDom.querySelectorAll('.promo_topicpromo__card-sliding'));
        this.slides = slideContainers.map((slideDom) => new TopicpromoCinemaSlide(slideDom))
        this.slides.forEach((slide) => {
            slide.registerClickEventHandler(() => this.trackClick(slide))
        })

        this.trackInitialLoadedTopicpromo();
        this.registerScrollTrackingObserver();


        const cinemaElement = topicpromoDom.querySelector("oc-cinema-v1");
        cinemaElement!.addEventListener("oc-item-visibility-changed", (event: OcCinemaV1Events["oc-item-visibility-changed"]) => {
            if (event.detail.scrollDirection === "right") {
                this.trackNextSlideTopicpromo(event.detail.visibleItems.indices, event.detail.stageIsScrolling);
            } else if (!event.detail.stageIsScrolling) {
                this.trackPreviousSlideTopicpromo();
            }
        });
    }

    trackedTilePositions: number[] = []

    registerScrollTrackingObserver(): void {
        /*                                     */
        const observer = new IntersectionObserver((entries) => {
            const slidesToTrack = this.getIntersectingSlides(entries);

            if (slidesToTrack.length > 0) {
                this.trackScroll(slidesToTrack);
                this.trackedTilePositions.push(...slidesToTrack.map(tile => tile.position));
            }

            slidesToTrack.forEach(slide => observer.unobserve(slide.slideDom));
        });

        this.slides.forEach((slide) => {
            observer.observe(slide.slideDom);
        })
    }

    getIntersectingSlides(entries: IntersectionObserverEntry[]): TopicpromoCinemaSlide[] {
        return entries
            .filter((entry) => entry.isIntersecting)
            .map((entry) => new TopicpromoCinemaSlide(entry.target as HTMLElement));
    }

    trackScroll(slidesToTrack: TopicpromoCinemaSlide[]): void {
        if (this.trackedTilePositions.length <= 0) {
            this.trackingService.sendMiniAction( this.featureIndex, slidesToTrack);
        }
    }

    trackInitialLoadedTopicpromo(): void {
        if (!this.domElement.getAttribute("data-tracked")) {
            this.domElement.setAttribute("data-tracked", String(true));
            this.trackingService.sendTopicpromoInitialLoadedTrackingEvent( this.featureOrder, this.featureIndex, this.slides, this.featureLabels);
        }
    }

    trackNextSlideTopicpromo(currentSlidePositions: number[], stageIsScrolling: boolean): void {
        const slides: TopicpromoCinemaSlide[] = []
        currentSlidePositions.forEach(position => {
            if (this.slides[position]) {
                slides.push(this.slides[position]);
            }
        });
        this.trackingService.sendTopicpromoSlideNextTrackingEvent( this.featureOrder, this.featureIndex, slides, this.featureLabels, stageIsScrolling);
    }

    trackPreviousSlideTopicpromo(): void {
        this.trackingService.sendTopicpromoSlidePreviousTrackingEvent( this.featureOrder, this.featureIndex, this.featureLabels);
    }

    trackClick(slide: TopicpromoCinemaSlide) {
        this.trackingService.sendTopicpromoClickTrackingEvent( this.featureOrder, this.featureIndex, slide, this.featureLabels);
    }

    private getFeatureOrder(container: Element): number {
        const dataFeatureOrder = container.closest('[data-feature-order]')?.getAttribute('data-feature-order');
        return dataFeatureOrder ? parseInt(dataFeatureOrder, 0) : -1;
    }

    private getFeatureIndex(container: Element): string {
        return container.getAttribute('data-feature-index') || "0";
    }

    private getFeatureLabels(container: Element): FeatureTrackingLabels {
        return JSON.parse(container.getAttribute('data-feature-tracking-labels')!);
    }

}